import { stringifyGraph } from '@/clients/http'
import { ProductModel as ProductApi } from '@/http/models/Product/Product'
import { BranchModel, type BranchOption } from '@/http/models/Branch'
import { useAuth } from '@/contexts/AuthContext'
import { format } from 'date-fns'
import { CalendarIcon, ChevronDown, X } from 'lucide-react'

import { useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { fieldClass } from '@/components/ui/field-class'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'

import { ProductModel as ProductModelTypes } from '@/types'
import { Spinner } from '@/components/ui/spinner'

interface EntryRow {
  product_variation_id: string
  variationName: string
  currentStock: number
  quantity: string
  purchase_price: string
  sale_price: string
  expiry_date: string
  batch_no: string
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (body: any) => void
  loading: boolean
}

export const PurchaseDialog = ({ open, onOpenChange, onSubmit, loading }: Props) => {
  const [description, setDescription] = useState('')
  const [entries, setEntries] = useState<EntryRow[]>([])
  // The branch the stock arrives at; the server otherwise uses the staff member's till branch (checklist C27).
  const [branches, setBranches] = useState<BranchOption[]>([])
  const [branchId, setBranchId] = useState('')
  const canListBranches = useAuth().can('branches:list')

  const [popoverOpen, setPopoverOpen] = useState(false)
  const [products, setProducts] = useState<ProductModelTypes.Product[]>([])
  const [productsLoading, setProductsLoading] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<ProductModelTypes.Product | null>(null)
  const [variations, setVariations] = useState<any[]>([])
  const [variationsLoading, setVariationsLoading] = useState(false)

  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (open) {
      setDescription('')
      setEntries([])
      setSelectedProduct(null)
      setVariations([])
      setProducts([])
      loadProducts('')
      if (canListBranches) BranchModel.getOptions().then((res) => {
        const list = res.success ? (res.data?.result ?? []) : []
        setBranches(list)
        setBranchId(list.length === 1 ? list[0]._id : '')
      })
    }
  }, [open])

  const loadProducts = async (search: string) => {
    setProductsLoading(true)
    const res = await ProductApi.getList({ query: { search: search || undefined, length: 30 } })
    setProducts(res.success ? (res.data?.result ?? []) : [])
    setProductsLoading(false)
  }

  const handleProductSearch = (value: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => loadProducts(value), 300)
  }

  const handleProductSelect = async (product: ProductModelTypes.Product) => {
    setPopoverOpen(false)
    setSelectedProduct(product)
    setVariations([])
    setVariationsLoading(true)

    const res = await ProductApi.getById({
      params: { id: product._id },
      query: {
        graph_enc: stringifyGraph({
          variations: {
            '*': true,
            'stocks(quantity!=0)': { _id: true, quantity: true },
          },
        }),
      },
    })

    const vars = res.success ? ((res.data?.result as any)?.variations ?? []) : []
    setVariations(vars)
    setVariationsLoading(false)
  }

  const isChecked = (variationId: string) =>
    entries.some((e) => e.product_variation_id === variationId)

  const toggleVariation = (variation: any) => {
    const id = variation._id
    if (isChecked(id)) {
      setEntries((prev) => prev.filter((e) => e.product_variation_id !== id))
    } else {
      const currentStock = (variation.stocks ?? []).reduce(
        (sum: number, s: any) => sum + s.quantity,
        0
      )
      setEntries((prev) => [
        ...prev,
        {
          product_variation_id: id,
          variationName: variation.name,
          currentStock,
          quantity: '',
          purchase_price: String(variation.purchase_cost ?? ''),
          sale_price: String(variation.regular_price ?? ''),
          expiry_date: '',
          batch_no: '',
        },
      ])
    }
  }

  const updateEntry = (id: string, patch: Partial<EntryRow>) =>
    setEntries((prev) => prev.map((e) => (e.product_variation_id === id ? { ...e, ...patch } : e)))

  const removeEntry = (id: string) =>
    setEntries((prev) => prev.filter((e) => e.product_variation_id !== id))

  const isValid =
    (branches.length <= 1 || !!branchId) &&
    description.trim().length > 0 &&
    entries.length > 0 &&
    entries.every(
      (e) => Number(e.quantity) > 0 && Number(e.purchase_price) >= 0 && Number(e.sale_price) >= 0
    )

  const handleSubmit = () => {
    if (!isValid) return
    onSubmit({
      ...(branchId ? { branch_id: branchId } : {}),
      description,
      image_ids: [],
      entries: entries.map((e) => ({
        product_variation_id: e.product_variation_id,
        quantity: Number(e.quantity),
        purchase_price: Number(e.purchase_price),
        sale_price: Number(e.sale_price),
        ...(e.expiry_date ? { expiry_date: e.expiry_date } : {}),
        ...(e.batch_no ? { batch_no: e.batch_no } : {}),
      })),
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl">
        <DialogHeader>
          <DialogTitle>New purchase</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {branches.length > 1 && (
            <div className="space-y-1.5">
              <Label htmlFor="purchase-branch">Branch</Label>
              <Select value={branchId} onValueChange={setBranchId}>
                <SelectTrigger id="purchase-branch" className="sm:w-72">
                  <SelectValue placeholder="Branch receiving the stock" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((b) => (
                    <SelectItem key={b._id} value={b._id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Description */}
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Initial stock purchase"
              rows={2}
            />
          </div>

          <Separator className="bg-border-subtle" />

          {/* Product search */}
          <div className="space-y-1.5">
            <Label>Product</Label>
            <Popover
              open={popoverOpen}
              onOpenChange={(val) => {
                setPopoverOpen(val)
                if (val && products.length === 0) loadProducts('')
              }}
              modal
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className={cn(
                    fieldClass,
                    'h-8 justify-between font-normal',
                    !selectedProduct && 'text-muted-foreground'
                  )}
                >
                  <span className="truncate">
                    {selectedProduct?.name || 'Select a product to browse variations'}
                  </span>
                  {productsLoading ? (
                    <Spinner />
                  ) : (
                    <ChevronDown className="text-muted-foreground" aria-hidden="true" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search products" onValueChange={handleProductSearch} />
                  <CommandList>
                    {productsLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <Spinner className="size-4 text-muted-foreground" />
                      </div>
                    ) : (
                      <>
                        <CommandEmpty>No products found.</CommandEmpty>
                        <CommandGroup>
                          {products.map((p) => (
                            <CommandItem
                              key={p._id}
                              value={p.name}
                              onSelect={() => handleProductSelect(p)}
                            >
                              {p.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {/* Variation checklist */}
            {selectedProduct && (
              <div className="mt-3 overflow-hidden rounded-lg border border-border">
                <div className="border-b border-border-subtle bg-subdued px-4 py-2 text-xs font-medium text-muted-foreground">
                  {selectedProduct.name} — select variations to add
                </div>
                {variationsLoading ? (
                  <div className="flex items-center gap-2 px-4 py-4 text-sm text-muted-foreground">
                    <Spinner className="size-4" /> Loading variations…
                  </div>
                ) : variations.length === 0 ? (
                  <p className="px-4 py-4 text-sm text-muted-foreground">No variations found.</p>
                ) : (
                  <div className="divide-y divide-border-subtle">
                    {variations.map((v) => {
                      const currentStock = (v.stocks ?? []).reduce(
                        (sum: number, s: any) => sum + s.quantity,
                        0
                      )
                      const checked = isChecked(v._id)
                      return (
                        <div
                          key={v._id}
                          className={cn(
                            'flex cursor-pointer items-center gap-3 px-4 py-2.5 transition-colors hover:bg-subdued',
                            checked && 'bg-subdued'
                          )}
                          onClick={() => toggleVariation(v)}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleVariation(v)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">
                            {v.name}
                          </span>
                          <span className="hidden text-xs text-muted-foreground sm:inline">
                            {v.sku}
                          </span>
                          <Badge tone="soft" className="tabular-nums">
                            Stock: {currentStock}
                          </Badge>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Entries form */}
          {entries.length > 0 && (
            <>
              <Separator className="bg-border-subtle" />
              <div className="space-y-2">
                <Label>Selected variations</Label>

                <div className="overflow-x-auto">
                  <div className="min-w-[820px] space-y-1.5">
                    {/* Header row */}
                    <div className="grid grid-cols-[1.5fr_80px_90px_100px_100px_150px_120px_32px] gap-2 px-3 py-1">
                      {[
                        'Variation',
                        'Current',
                        'Quantity *',
                        'Cost *',
                        'Price *',
                        'Expiry',
                        'Batch',
                        '',
                      ].map((h) => (
                        <span key={h} className="text-xs font-medium text-muted-foreground">
                          {h}
                        </span>
                      ))}
                    </div>

                    <div className="space-y-1.5">
                      {entries.map((entry) => (
                        <div
                          key={entry.product_variation_id}
                          className="grid grid-cols-[1.5fr_80px_90px_100px_100px_150px_120px_32px] items-center gap-2 rounded-lg border border-border-subtle bg-subdued px-3 py-2"
                        >
                          <span
                            className="truncate text-sm font-medium text-ink"
                            title={entry.variationName}
                          >
                            {entry.variationName}
                          </span>
                          <span className="text-center text-sm tabular-nums text-muted-foreground">
                            {entry.currentStock}
                          </span>
                          <Input
                            type="number"
                            min="1"
                            placeholder="0"
                            value={entry.quantity}
                            onChange={(e) =>
                              updateEntry(entry.product_variation_id, { quantity: e.target.value })
                            }
                          />
                          <Input
                            type="number"
                            min="0"
                            placeholder="0.00"
                            value={entry.purchase_price}
                            onChange={(e) =>
                              updateEntry(entry.product_variation_id, {
                                purchase_price: e.target.value,
                              })
                            }
                          />
                          <Input
                            type="number"
                            min="0"
                            placeholder="0.00"
                            value={entry.sale_price}
                            onChange={(e) =>
                              updateEntry(entry.product_variation_id, {
                                sale_price: e.target.value,
                              })
                            }
                          />
                          <div className="flex items-center gap-1">
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="lg"
                                  className={cn(
                                    'flex-1 justify-start rounded-md px-2 text-left font-normal',
                                    !entry.expiry_date && 'text-muted-foreground'
                                  )}
                                >
                                  <CalendarIcon />
                                  {entry.expiry_date
                                    ? format(new Date(entry.expiry_date), 'dd MMM yy')
                                    : 'Pick date'}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={
                                    entry.expiry_date ? new Date(entry.expiry_date) : undefined
                                  }
                                  onSelect={(date) =>
                                    updateEntry(entry.product_variation_id, {
                                      expiry_date: date ? format(date, 'yyyy-MM-dd') : '',
                                    })
                                  }
                                  disabled={(date) => date < new Date()}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                            {entry.expiry_date && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label="Clear expiry date"
                                className="text-muted-foreground hover:text-destructive"
                                onClick={() =>
                                  updateEntry(entry.product_variation_id, { expiry_date: '' })
                                }
                              >
                                <X />
                              </Button>
                            )}
                          </div>
                          <Input
                            placeholder="Batch no."
                            value={entry.batch_no}
                            onChange={(e) =>
                              updateEntry(entry.product_variation_id, { batch_no: e.target.value })
                            }
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={`Remove ${entry.variationName}`}
                            className="text-muted-foreground hover:text-destructive"
                            onClick={() => removeEntry(entry.product_variation_id)}
                          >
                            <X />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || loading}>
            {loading ? (
              <>
                <Spinner />
                Saving…
              </>
            ) : (
              `Record purchase${entries.length > 0 ? ` (${entries.length})` : ''}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
